import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck, Clock, Search } from 'lucide-react';
import { getUsers, supervisorApproveUser } from '../../services/api';
import { useCurrentUser } from '../../context/UserContext';
import { UserEmail } from '../../components/common/UserEmail';

interface PendingUser {
  userId: string;
  displayName: string;
  email: string;
  phone?: string;
  approvalStatus: string;
  supervisorId?: string;
  createdAt: string;
}

/**
 * Supervisor's queue of pending users who registered using this supervisor's code.
 * Admins see all pending users (filter not applied) via the existing UsersPage.
 */
export default function PendingApprovalsPage() {
  const { userId: meId, role } = useCurrentUser();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [approveModal, setApproveModal] = useState<{ userId: string; name: string } | null>(null);
  const [approveRole, setApproveRole] = useState<'enumerator' | 'transcriber'>('enumerator');

  const { data, isLoading } = useQuery({
    queryKey: ['users-pending-supervisor', meId],
    queryFn: () => getUsers({ approvalStatus: 'pending' }),
    enabled: !!meId,
  });

  const approveMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'enumerator' | 'transcriber' }) =>
      supervisorApproveUser(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users-pending-supervisor', meId] });
      setApproveModal(null);
    },
  });

  const myPending = useMemo(() => {
    const all: PendingUser[] = data?.users ?? [];
    // Admins on this page see everyone (rare; this page is primarily supervisor's).
    const scoped = role === 'admin' ? all : all.filter((u) => u.supervisorId === meId);
    return scoped.filter(
      (u) =>
        u.displayName.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, meId, role, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>
        <p className="text-gray-500 text-sm mt-1">
          New users who signed up using your supervisor code — approve and assign as Enumerator or Transcriber.
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input pl-9"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</td></tr>
              ) : myPending.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    <Clock size={20} className="inline-block mr-2 text-gray-300" />
                    No pending approvals.
                  </td>
                </tr>
              ) : (
                myPending.map((u) => (
                  <tr key={u.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.displayName}</td>
                    <td className="px-4 py-3 text-sm"><UserEmail email={u.email} role="enumerator" /></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{u.phone ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setApproveModal({ userId: u.userId, name: u.displayName })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      >
                        <UserCheck size={14} />
                        Approve
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Approve User</h3>
            <p className="text-sm text-gray-500 mb-4">
              Approve <strong>{approveModal.name}</strong> and assign a role.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="input mb-4"
              value={approveRole}
              onChange={(e) => setApproveRole(e.target.value as 'enumerator' | 'transcriber')}
            >
              <option value="enumerator">Enumerator</option>
              <option value="transcriber">Transcriber</option>
            </select>
            <div className="flex gap-3">
              <button
                className="btn-primary flex-1"
                onClick={() =>
                  approveMutation.mutate({ userId: approveModal.userId, role: approveRole })
                }
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </button>
              <button className="btn-secondary flex-1" onClick={() => setApproveModal(null)}>
                Cancel
              </button>
            </div>
            {approveMutation.error && (
              <p className="text-red-600 text-xs mt-3">
                {(approveMutation.error as Error).message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
